import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { usePostRace, useRefetchAll, useUser } from '@utils/hooks/queries';
import PostRaceDialog from './PostRaceDialog';

/**
 * PRD 8 §3 — the post-race screen, raised at **app opening**, not at login: an athlete on a persisted
 * session never logs in again. Mounted app-level beside the other global siblings, so it fires on any
 * route and on a PWA cold start alike; `due` is decided entirely server-side, which is what lets one
 * rule cover both triggers (from the day after the race, and straight after the result is submitted).
 *
 * Excluded paths: onboarding, and the flow this dialog itself launches — it must never stack on top of
 * the screen it sent the athlete to.
 */
const EXCLUDED = ['/onboarding', '/new-objective'];

export const PostRaceGate: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { user } = useUser();
  const { state, markShown, postpone, choose, isSubmitting } = usePostRace();
  const { refetchAll } = useRefetchAll();
  const location = useLocation();

  if (!isAuthenticated || !user?.plan_v2_id) return null;
  if (EXCLUDED.some((path) => location.pathname.startsWith(path))) return null;
  if (!state?.due) return null;

  const onNoObjective = async () => {
    await choose('background');
    await refetchAll();
  };

  return (
    <PostRaceDialog
      state={state}
      onShown={() => void markShown()}
      onNewRace={() => void choose('race')}
      onNoObjective={onNoObjective}
      onPostpone={() => void postpone()}
      busy={isSubmitting}
    />
  );
};

export default PostRaceGate;
